import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateHreflangTags } from '../../utils/hreflang';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    // Update meta tags
    document.title = 'Kidney Transplant Abroad Support | Transparent Cost & Ethical Coordination';
    
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Meta tags
    updateMetaTag('description', 'Specialized in international kidney transplant coordination and cost guidance. Transparent support for patients exploring kidney transplant abroad. All medical decisions remain with licensed physicians.');
    updateMetaTag('keywords', 'kidney transplant abroad, kidney transplant cost, international kidney transplant, kidney transplant coordination, transplant cost guidance');
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);
    
    // Canonical URL
    updateLinkTag('canonical', 'https://kidneypatientsupport.com/');
    
    // Hreflang tags
    updateHreflangTags({
      enPath: '/',
      jaPath: '/' // Japanese homepage exists
    });
    
    // Open Graph
    updateMetaTag('og:url', 'https://kidneypatientsupport.com/', true);
    updateMetaTag('og:title', 'Kidney Transplant Abroad | International Coordination & Cost Guidance', true);
    updateMetaTag('og:description', 'Specialized in international kidney transplant coordination and cost guidance. Transparent support for patients exploring kidney transplant abroad.', true);
    updateMetaTag('og:type', 'website', true);
    
    // Twitter
    updateMetaTag('twitter:url', 'https://kidneypatientsupport.com/');
    updateMetaTag('twitter:title', 'Kidney Transplant Abroad | International Coordination & Cost Guidance');
    updateMetaTag('twitter:description', 'Specialized in international kidney transplant coordination and cost guidance. Transparent support for patients exploring kidney transplant abroad.');
    updateMetaTag('twitter:card', 'summary_large_image');

    // Add structured data scripts
    const scripts: HTMLScriptElement[] = [];

    // WebSite Schema
    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://kidneypatientsupport.com/#website",
      "name": "Kidney Patient Support",
      "url": "https://kidneypatientsupport.com/",
      "description": "Specialized in international kidney transplant coordination and cost guidance for patients exploring kidney transplant abroad.",
      "inLanguage": "en",
      "publisher": {
        "@id": "https://kidneypatientsupport.com/#organization"
      }
    });
    document.head.appendChild(websiteScript);
    scripts.push(websiteScript);

    // Organization Schema
    const organizationScript = document.createElement('script');
    organizationScript.type = 'application/ld+json';
    organizationScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://kidneypatientsupport.com/#organization",
      "name": "Kidney Patient Support",
      "alternateName": "Japan Ambis International",
      "url": "https://kidneypatientsupport.com/",
      "description": "Specialized in international kidney transplant coordination and cost guidance. We facilitate communication between patients and licensed transplant hospitals abroad. We do not perform medical procedures or provide medical advice.",
      "disambiguatingDescription": "Kidney transplant coordination support organization specializing in international transplant cost guidance and logistical support. This is NOT a medical provider, hospital, or clinic.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "kidney transplant coordination support",
        "email": "info@anbis-int.com",
        "telephone": "+81-050-6866-3030",
        "availableLanguage": ["English", "Japanese"]
      },
      "serviceType": "International kidney transplant coordination",
      "areaServed": {
        "@type": "Place",
        "name": "International"
      },
      "knowsAbout": [
        "Kidney transplant abroad coordination",
        "International kidney transplant cost guidance",
        "Kidney transplant process support",
        "Hospital communication facilitation for kidney transplant patients"
      ],
      "additionalType": "https://schema.org/LocalBusiness",
      "slogan": "Specialized coordination and cost guidance for international kidney transplant patients"
    });
    document.head.appendChild(organizationScript);
    scripts.push(organizationScript);

    // BreadcrumbList Schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://kidneypatientsupport.com/"
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);
    scripts.push(breadcrumbScript);

    // WebPage Schema
    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Kidney Transplant Abroad | International Coordination & Cost Guidance",
      "description": "Specialized in international kidney transplant coordination and cost guidance. Transparent support for patients exploring kidney transplant abroad.",
      "url": "https://kidneypatientsupport.com/",
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://kidneypatientsupport.com/#website"
      },
      "about": {
        "@id": "https://kidneypatientsupport.com/#organization"
      },
      "publisher": {
        "@id": "https://kidneypatientsupport.com/#organization"
      }
    });
    document.head.appendChild(webPageScript);
    scripts.push(webPageScript);

    // FAQPage Schema - NEW SECTION
    const faqPageScript = document.createElement('script');
    faqPageScript.type = 'application/ld+json';
    faqPageScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How long does kidney transplant abroad take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The timeline for kidney transplant abroad varies by country and individual medical situation. Living-donor transplants can typically be scheduled within 2-4 months after initial evaluation and approval. Deceased-donor transplants depend on waiting list availability in the destination country. We coordinate with licensed hospitals to provide realistic timeline estimates based on your specific circumstances."
          }
        },
        {
          "@type": "Question",
          "name": "How much does kidney transplant cost overseas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Kidney transplant cost overseas varies significantly by country. Estimated ranges include: Pakistan ($15,000-$25,000), India ($20,000-$35,000), Turkey ($40,000-$60,000), and Mexico ($50,000-$80,000). These are general estimates for planning purposes only. Final medical costs are determined by the treating hospital based on individual clinical needs. Visit our cost comparison page for detailed breakdowns."
          }
        },
        {
          "@type": "Question",
          "name": "Which countries perform kidney transplants for international patients?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Several countries have established kidney transplant programs that accept international patients, including Pakistan, India, Turkey, Mexico, Thailand, and the Philippines. Each country has different regulatory frameworks, cost structures, and waiting times. We coordinate exclusively with licensed hospitals in countries with recognized transplant programs. Visit our Countries page to compare options and understand each destination's specific requirements."
          }
        },
        {
          "@type": "Question",
          "name": "Is kidney transplant abroad legal?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, kidney transplant abroad is legal when conducted through licensed hospitals following local transplant regulations. We coordinate only within legal frameworks and comply with international ethical standards including the Declaration of Istanbul. All donor arrangements must follow applicable laws prohibiting organ trade. Patients should consult legal and medical advisors in their home country before proceeding. Read our Ethics page for our complete legal and ethical framework."
          }
        }
      ]
    });
    document.head.appendChild(faqPageScript);
    scripts.push(faqPageScript);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      scripts.forEach(script => {
        if (script.parentNode) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d513hao9ei8qjpb9for0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/f1b13f3c5cdde047774c7297e509c16c.png" alt="Kidney Patient Support" className="h-12 w-auto" />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600">Home</Link>
            <Link to="/ethics" className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600">Ethics</Link>
            <Link to="/complete-guide-kidney-transplant-abroad" className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600">Guide</Link>
            <Link to="/countries" className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600">Countries</Link>
            <Link to="/pricing" className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600">Pricing</Link>
            <Link to="/process" className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600">Process</Link>
            <Link to="/contact" className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600">Contact</Link>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer z-[60]"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>

        {/* Mobile Menu Drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-[55] overflow-y-auto">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Home</Link>
              <Link to="/ethics" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Ethics</Link>
              <Link to="/complete-guide-kidney-transplant-abroad" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Guide</Link>
              <Link to="/countries" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Countries</Link>
              <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Pricing</Link>
              <Link to="/process" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Process</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/042a6537be929eb43da307974ac430d9.jpg"
            alt="International kidney transplant coordination and cost guidance consultation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative h-full min-h-screen flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full py-32">
            <div className="max-w-4xl">
              <h1 className="text-6xl md:text-7xl font-bold text-white mb-8 leading-tight">
                Kidney Transplant Abroad — Coordination &amp; Cost Guidance
              </h1>
              <div className="bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl p-8 mb-8">
                <p className="text-xl text-white/95 mb-4 leading-relaxed">
                  We specialize in <strong>international kidney transplant</strong> coordination — helping patients navigate <strong>kidney transplant cost</strong>, hospital communication, and logistical planning when considering <strong>kidney transplant abroad</strong>.
                </p>
                <div className="space-y-2 text-white/90 text-base">
                  <p className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-400 mr-3 mt-1 flex-shrink-0"></i>
                    <span>Focused exclusively on kidney transplant coordination and cost guidance</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-400 mr-3 mt-1 flex-shrink-0"></i>
                    <span>Informational and logistical support for international transplant options</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-400 mr-3 mt-1 flex-shrink-0"></i>
                    <span>All procedures performed exclusively by licensed hospitals and qualified transplant surgeons</span>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center bg-teal-600 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-teal-700 transition-colors space-x-3 whitespace-nowrap cursor-pointer shadow-xl"
                >
                  <span>Request Transplant Information</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link 
                  to="/kidney-transplant-cost-abroad"
                  className="inline-flex items-center justify-center bg-white/20 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-white/30 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
                >
                  <span>View Cost Guidance</span>
                  <i className="ri-money-dollar-circle-line"></i>
                </Link>
              </div>
              
              <p className="text-white/80 text-sm">
                No medical advice provided. Coordination and informational support only.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Focus on Kidney Transplant Support */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Focus on Kidney Transplant Support</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-6">
              This organization exists for one purpose: to support patients exploring <strong>kidney transplant abroad</strong>. We do not cover general nephrology topics or unrelated medical services. Our entire operation is built around <strong>international kidney transplant</strong> coordination and <strong>kidney transplant cost</strong> transparency.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-6">
              For a detailed overview of costs, waiting time, eligibility, and ethical considerations, review our <Link to="/complete-guide-kidney-transplant-abroad" className="text-teal-600 hover:text-teal-700 font-medium">complete guide to kidney transplant abroad</Link>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-global-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">International Kidney Transplant Options</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                We help patients understand <Link to="/countries" className="text-teal-600 hover:text-teal-700 font-medium">international kidney transplant</Link> pathways, including programs in <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">Pakistan</Link> and other countries with established transplant infrastructure.
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-money-dollar-circle-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Kidney Transplant Cost Guidance</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Transparent <Link to="/kidney-transplant-cost-abroad" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant cost</Link> estimates help patients plan financially. We provide cost ranges across countries so patients can make informed comparisons.
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border border-teal-100 text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-time-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Transplant Timing &amp; Coordination</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Understanding <Link to="/transplant-waiting-time-abroad" className="text-teal-600 hover:text-teal-700 font-medium">transplant waiting times abroad</Link> is critical. We coordinate with licensed hospitals to help patients navigate scheduling based on medical eligibility.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-200 text-center">
            <p className="text-gray-700 text-base leading-relaxed">
              <strong>We do not provide medical treatment or advice.</strong> All medical decisions are made solely by licensed physicians at partner hospitals.
            </p>
          </div>
        </div>
      </section>

      {/* Our Role Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">How We Support Kidney Transplant Patients</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-4">
              Our coordination role is specifically designed for patients considering kidney transplant abroad:
            </p>
            <p className="text-gray-700 text-base leading-relaxed max-w-3xl mx-auto">
              Before making any decisions, we encourage patients to review how the <Link to="/process" className="text-teal-600 hover:text-teal-700 font-medium">international kidney transplant process</Link> works and understand each step of the coordination journey.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-start">
                <i className="ri-information-line text-teal-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Kidney transplant pathway information</h3>
                  <p className="text-gray-600 text-sm">General guidance on international kidney transplant options, including <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant in Pakistan</Link> and other established programs.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-start">
                <i className="ri-hospital-line text-teal-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Hospital communication facilitation</h3>
                  <p className="text-gray-600 text-sm">We facilitate communication between patients and licensed transplant hospitals upon request.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-start">
                <i className="ri-money-dollar-circle-line text-teal-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Transparent kidney transplant cost guidance</h3>
                  <p className="text-gray-600 text-sm">Estimated <Link to="/kidney-transplant-cost-abroad" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant cost abroad</Link> ranges for financial planning purposes.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-start">
                <i className="ri-map-pin-line text-teal-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Logistical preparation support</h3>
                  <p className="text-gray-600 text-sm">Travel, accommodation, and scheduling coordination for patients pursuing kidney transplant abroad.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-700 text-base leading-relaxed">
              <strong>All medical advice, diagnosis, and treatment decisions are made solely by licensed physicians.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Why Patients Consider Kidney Transplant Abroad */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Patients Consider Kidney Transplant Abroad</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-6">
              Every year, thousands of patients explore <strong>kidney transplant abroad</strong> as an alternative to long domestic waiting lists. Understanding the reasons behind this decision is an important first step in evaluating whether <strong>international kidney transplant</strong> coordination support may be appropriate for your situation.
            </p>
          </div>

          <div className="space-y-6 mb-10">
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Domestic Waiting Lists and Limited Availability</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                In countries like the United States, the United Kingdom, and Australia, kidney transplant waiting lists can extend from three to over five years. During this time, patients remain on dialysis, which carries its own health risks and financial burden. For many, the prospect of years on a <Link to="/transplant-waiting-time-abroad" className="text-teal-600 hover:text-teal-700 font-medium">transplant waiting list</Link> motivates them to explore international programs where scheduling may be more flexible based on medical eligibility.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Financial Planning and Cost Transparency</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                The cost of kidney transplant varies significantly across countries. Patients who are uninsured, underinsured, or facing high out-of-pocket expenses at home may find that international programs offer a more manageable financial pathway. Our <Link to="/kidney-transplant-cost-abroad" className="text-teal-600 hover:text-teal-700 font-medium">cost comparison for kidney transplant abroad</Link> provides estimated ranges across multiple countries to support informed financial planning. All cost guidance is for planning purposes only, and final pricing is determined by the treating hospital.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Access to Licensed International Hospitals</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                International transplant programs at licensed hospitals employ qualified transplant surgeons and follow local regulatory frameworks. Patients considering kidney transplant abroad are not compromising on medical standards — they are accessing established healthcare systems in countries with experienced transplant centers. We coordinate exclusively with licensed hospitals, and all medical decisions remain with the treating physicians.
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Exploring international options should always begin with a consultation with your own physician.
            </p>
          </div>
        </div>
      </section>

      {/* Transparent Cost Overview Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Kidney Transplant Cost — Transparent Overview</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-4">
              Understanding <strong>kidney transplant cost</strong> is essential for patients considering treatment abroad. Estimated costs vary depending on country, hospital, and clinical situation.
            </p>
            <p className="text-gray-700 text-base leading-relaxed max-w-3xl mx-auto">
              Patients exploring <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant in Pakistan</Link> can review our detailed <Link to="/kidney-transplant-cost-abroad" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant cost abroad</Link> comparison, or visit our <Link to="/pricing" className="text-teal-600 hover:text-teal-700 font-medium">transparent pricing page</Link> for coordination fee guidance.
            </p>
          </div>
          
          <div className="bg-white rounded-3xl p-10 border border-gray-200 mb-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">We provide:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start text-gray-700 text-base">
                    <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span>General cost guidance for planning purposes</span>
                  </li>
                  <li className="flex items-start text-gray-700 text-base">
                    <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span>No hidden fees</span>
                  </li>
                  <li className="flex items-start text-gray-700 text-base">
                    <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                    <span>No outcome guarantees</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Important:</h3>
                <p className="text-gray-700 text-base leading-relaxed">
                  Final medical pricing is determined directly by the treating hospital.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link 
              to="/pricing"
              className="inline-flex items-center bg-teal-600 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-teal-700 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
            >
              <span>View Detailed Pricing</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Considering Your Options Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Exploring Kidney Transplant Options Abroad?</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-4">
              If you are considering <strong>kidney transplant abroad</strong>, you may request general information about the coordination process, <Link to="/kidney-transplant-cost-abroad" className="text-teal-600 hover:text-teal-700 font-medium">international kidney transplant costs</Link>, and next steps.
            </p>
            <p className="text-gray-700 text-base leading-relaxed max-w-3xl mx-auto mb-8">
              We recommend reviewing our <Link to="/who-this-is-for" className="text-teal-600 hover:text-teal-700 font-medium">eligibility guide</Link> to determine if this kidney transplant coordination service is appropriate for your situation, and understanding our <Link to="/ethics" className="text-teal-600 hover:text-teal-700 font-medium">ethical framework</Link> that governs all coordination activities.
            </p>
            
            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 max-w-4xl mx-auto">
              <div className="flex items-center justify-center space-x-2 bg-teal-50 rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">Transparent Cost Guidance</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-teal-50 rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">Ethical Framework</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-teal-50 rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">Licensed Hospitals Only</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-teal-50 rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">No Hidden Fees</span>
              </div>
            </div>
            
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center bg-teal-600 text-white px-10 py-5 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors space-x-3 whitespace-nowrap cursor-pointer shadow-xl"
            >
              <span>Receive Information Guide</span>
              <i className="ri-file-text-line"></i>
            </Link>
            
            <p className="text-gray-600 text-sm mt-6 max-w-2xl mx-auto">
              We do not provide medical advice. All treatment decisions remain with licensed physicians.
            </p>
          </div>
        </div>
      </section>

      {/* Ethical & Legal Commitment Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Ethical &amp; Legal Commitment</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-8">
              We operate under strict ethical principles.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 border border-green-200">
              <div className="flex items-start">
                <i className="ri-close-circle-line text-red-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">No organ trade</h3>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 border border-green-200">
              <div className="flex items-start">
                <i className="ri-close-circle-line text-red-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">No illegal donor arrangements</h3>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 border border-green-200">
              <div className="flex items-start">
                <i className="ri-close-circle-line text-red-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">No commercialization of organs</h3>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 border border-green-200">
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-green-600 text-3xl mr-4 flex-shrink-0 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Compliance with local transplant laws</h3>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link 
              to="/ethics"
              className="inline-flex items-center text-teal-600 font-semibold text-lg hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span>Read more on our Ethics &amp; Legal Framework page</span>
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Important Notice Section */}
      <section className="py-20 bg-yellow-50 border-y border-yellow-200">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Important Notice</h2>
            <div className="bg-white rounded-3xl p-10 border border-yellow-200">
              <p className="text-gray-700 text-xl leading-relaxed mb-6">
                <strong>This website provides informational support only.</strong>
              </p>
              <div className="space-y-4 max-w-2xl mx-auto">
                <p className="flex items-start text-gray-700 text-base justify-center">
                  <i className="ri-close-circle-line text-red-600 mt-1 mr-3 flex-shrink-0 text-xl"></i>
                  <span>It does not constitute medical advice.</span>
                </p>
                <p className="flex items-start text-gray-700 text-base justify-center">
                  <i className="ri-close-circle-line text-red-600 mt-1 mr-3 flex-shrink-0 text-xl"></i>
                  <span>It does not replace consultation with licensed medical professionals.</span>
                </p>
              </div>
              <p className="text-gray-700 text-xl leading-relaxed mt-8">
                <strong>Patients must consult qualified transplant specialists before making decisions.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Is This Right for You? Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Is Kidney Transplant Abroad Right for You?</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto mb-10">
              Before exploring <strong>international kidney transplant</strong> options, we encourage you to review whether this coordination service fits your situation. Patients considering <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-medium">kidney transplant in Pakistan</Link> or other countries can start by understanding <Link to="/transplant-waiting-time-abroad" className="text-teal-600 hover:text-teal-700 font-medium">transplant waiting times abroad</Link>.
            </p>
            <Link 
              to="/who-this-is-for"
              className="inline-flex items-center justify-center bg-teal-600 text-white px-10 py-5 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors space-x-3 whitespace-nowrap cursor-pointer shadow-xl"
            >
              <span>Check Eligibility</span>
              <i className="ri-user-search-line"></i>
            </Link>
            <p className="text-gray-600 text-sm mt-6 max-w-2xl mx-auto">
              This service provides coordination and information support only. All medical decisions remain with licensed physicians.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section - OLD (Keep existing) */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Kidney Transplant Abroad — Common Questions</h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: "Do you perform kidney transplant surgeries?",
                answer: "No. We provide coordination and informational support only. All kidney transplant procedures are conducted by licensed hospitals and qualified transplant surgeons."
              },
              {
                question: "Do you guarantee kidney transplant success?",
                answer: "No. Transplant outcomes depend on individual medical factors. We provide transparent cost guidance and coordination support, but all medical decisions and outcomes are determined by licensed physicians."
              },
              {
                question: "Do you arrange donors for kidney transplant?",
                answer: "We only coordinate within the framework of applicable transplant regulations. We do not engage in organ trade or illegal donor arrangements."
              },
              {
                question: "What countries do you coordinate kidney transplant in?",
                answer: "We currently coordinate with licensed hospitals in Pakistan and other countries with established kidney transplant programs. Visit our Countries page for more details."
              },
              {
                question: "How much does kidney transplant abroad cost?",
                answer: "Kidney transplant cost varies by country, hospital, and clinical situation. We provide estimated cost ranges for planning purposes. Final pricing is determined by the treating hospital."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">
                    Q: {faq.question}
                  </h3>
                  <i className={`ri-arrow-${openFaqIndex === index ? 'up' : 'down'}-s-line text-2xl text-teal-600 flex-shrink-0`}></i>
                </button>
                {openFaqIndex === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 text-base leading-relaxed">
                      <strong>A:</strong> {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW FAQ Section - Frequently Asked Questions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Common questions about kidney transplant abroad, costs, timelines, and legal considerations.
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: "How long does kidney transplant abroad take?",
                answer: "The timeline for kidney transplant abroad varies by country and individual medical situation. Living-donor transplants can typically be scheduled within 2-4 months after initial evaluation and approval. Deceased-donor transplants depend on waiting list availability in the destination country. We coordinate with licensed hospitals to provide realistic timeline estimates based on your specific circumstances."
              },
              {
                question: "How much does kidney transplant cost overseas?",
                answer: "Kidney transplant cost overseas varies significantly by country. Estimated ranges include: Pakistan ($15,000-$25,000), India ($20,000-$35,000), Turkey ($40,000-$60,000), and Mexico ($50,000-$80,000). These are general estimates for planning purposes only. Final medical costs are determined by the treating hospital based on individual clinical needs. Visit our cost comparison page for detailed breakdowns."
              },
              {
                question: "Which countries perform kidney transplants for international patients?",
                answer: "Several countries have established kidney transplant programs that accept international patients, including Pakistan, India, Turkey, Mexico, Thailand, and the Philippines. Each country has different regulatory frameworks, cost structures, and waiting times. We coordinate exclusively with licensed hospitals in countries with recognized transplant programs. Visit our Countries page to compare options and understand each destination's specific requirements."
              },
              {
                question: "Is kidney transplant abroad legal?",
                answer: "Yes, kidney transplant abroad is legal when conducted through licensed hospitals following local transplant regulations. We coordinate only within legal frameworks and comply with international ethical standards including the Declaration of Istanbul. All donor arrangements must follow applicable laws prohibiting organ trade. Patients should consult legal and medical advisors in their home country before proceeding. Read our Ethics page for our complete legal and ethical framework."
              }
            ].map((faq, index) => (
              <div key={index + 100} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === (index + 100) ? null : (index + 100))}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${openFaqIndex === (index + 100) ? 'up' : 'down'}-s-line text-2xl text-teal-600 flex-shrink-0`}></i>
                </button>
                {openFaqIndex === (index + 100) && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ready to Explore <span className="text-teal-600">Kidney Transplant Abroad?</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-2">
              Submit your information request and we will provide transparent guidance about kidney transplant coordination, cost estimates, and next steps.
            </p>
            <p className="text-gray-500 text-sm">
              Coordination and informational support only. All medical decisions remain with licensed physicians.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-teal-600"></i>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
                    <p className="text-gray-600 leading-relaxed">
                      +81-050-6866-3030<br />
                      Mobile: +81-90-7803-0727
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-teal-600"></i>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
                    <p className="text-gray-600 leading-relaxed">
                      info@anbis-int.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <form id="contact-form" data-readdy-form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="How can we help?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us more about your inquiry..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
                    Sorry, there was an error. Please try again.
                  </div>
                )}
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-teal-600 text-white px-6 py-4 rounded-xl text-base font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <span>{formStatus === 'submitting' ? 'Sending...' : 'Start Information Request'}</span>
                  <i className="ri-send-plane-fill"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-white/90 text-base leading-relaxed max-w-4xl mx-auto">
              <strong>This website provides coordination and informational support services only.</strong><br />
              Medical decisions and procedures are handled exclusively by licensed healthcare providers.
            </p>
          </div>

          {/* Core Pages */}
          <div className="border-t border-white/20 pt-8 mb-8">
            <h4 className="text-white font-bold text-sm tracking-wide mb-4 text-center">CORE PAGES</h4>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
              <Link to="/pakistan" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Kidney Transplant in Pakistan</Link>
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Transparent Cost Overview</Link>
              <Link to="/process" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">How the Process Works</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethical Framework</Link>
              <Link to="/who-this-is-for" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Eligibility Guide</Link>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/80 text-sm mb-4 md:mb-0">
              © 2025 Japan Ambis International. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</Link>
              <Link to="/ethics" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Ethics</Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}